import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { COLOR, FONT, RADIUS, SPACE, Z } from "../lib/tokens";
import { paletteEntry } from "../lib/participantPalette";

// AssignStepSheet — bottom sheet for offering ONE step to one or more
// participants on the current cook session. Multi-select: the lead can
// invite several people and whoever marks done first claims the step
// (status-fan trigger closes the siblings).
//
// Props:
//   open                — bool
//   onClose             — fn()
//   step                — { id, title }
//   participants        — array of cook_session_participants rows
//   existingAssignments — current cook_step_assignments rows for THIS step
//                         (used to render status chips next to each
//                         participant so the lead doesn't re-offer to
//                         someone who already accepted/declined)
//   profilesById        — Map<user_id, { name, avatar_url }> for labels
//   defaultDeadline     — Date | string (ISO) — auto-computed by caller
//   selfUserId          — current user, hidden from the picker
//   onAssign            — async ({ assigneeIds, deadline }) → null

const FIELD_TIME_STYLE = {
  fontFamily: FONT.mono,
  fontSize: 13,
  color: COLOR.ink,
  background: COLOR.ground,
  border: `1px solid ${COLOR.border}`,
  borderRadius: RADIUS.md,
  padding: "10px 12px",
  width: "100%",
  outline: "none",
};

function statusBadge(status) {
  switch (status) {
    case "pending":   return { label: "PENDING",  fg: COLOR.muted, bg: COLOR.deep };
    case "accepted":  return { label: "ACCEPTED", fg: "#fff",      bg: COLOR.leaf };
    case "declined":  return { label: "DECLINED", fg: COLOR.muted, bg: COLOR.deep };
    case "done":      return { label: "DONE",     fg: "#fff",      bg: COLOR.leaf };
    case "in_progress": return { label: "DOING",  fg: "#fff",      bg: COLOR.sky };
    case "overdue":   return { label: "OVERDUE",  fg: "#fff",      bg: COLOR.rose };
    case "skipped":   return { label: "SKIPPED",  fg: COLOR.muted, bg: COLOR.deep };
    default:          return null;
  }
}

function toLocalInputValue(date) {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AssignStepSheet({
  open,
  onClose,
  step,
  participants = [],
  existingAssignments = [],
  profilesById,
  defaultDeadline = null,
  selfUserId,
  onAssign,
}) {
  const [selected, setSelected] = useState(new Set());
  const [deadlineStr, setDeadlineStr] = useState(toLocalInputValue(defaultDeadline));
  const [busy, setBusy] = useState(false);

  // Filter: drop self + already-DONE rows (no point re-offering a closed
  // step). Diners are invitable too in case the lead wants them to
  // accept a co-cook role for one step.
  const candidates = useMemo(() => {
    const byUser = new Map();
    for (const a of existingAssignments || []) byUser.set(a.assigned_to, a);
    return (participants || [])
      .filter((p) => p.user_id !== selfUserId)
      .filter((p) => !p.left_at)
      .map((p) => ({
        participant: p,
        existing: byUser.get(p.user_id) || null,
      }));
  }, [participants, existingAssignments, selfUserId]);

  if (!open) return null;

  const toggle = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const confirm = async () => {
    if (!onAssign || selected.size === 0) return;
    setBusy(true);
    try {
      const deadlineIso = deadlineStr
        ? new Date(deadlineStr).toISOString()
        : null;
      await onAssign({
        assigneeIds: Array.from(selected),
        deadline: deadlineIso,
      });
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  // Portal to body: CookMode wraps in a transformed motion.div, which
  // would otherwise make position:fixed resolve against the parent and
  // push the bottom-anchored panel offscreen.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,20,20,0.45)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: Z.sheet,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          background: COLOR.night,
          borderTopLeftRadius: RADIUS.sheet,
          borderTopRightRadius: RADIUS.sheet,
          padding: `${SPACE.section}px ${SPACE.section}px calc(${SPACE.hero}px + env(safe-area-inset-bottom))`,
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <div style={{
          fontFamily: FONT.mono,
          fontSize: 10,
          letterSpacing: 1.2,
          color: COLOR.muted,
          marginBottom: SPACE.xs,
        }}>
          ASSIGN STEP
        </div>
        <div style={{
          fontFamily: FONT.itemTitle,
          fontStyle: "italic",
          fontSize: 22,
          color: COLOR.ink,
          lineHeight: 1.2,
          marginBottom: SPACE.section,
        }}>
          {step?.title || `Step ${step?.id}`}
        </div>

        <div style={{
          fontFamily: FONT.mono,
          fontSize: 10,
          letterSpacing: 1.2,
          color: COLOR.muted,
          marginBottom: SPACE.sm,
        }}>
          TEAMMATES
        </div>

        {candidates.length === 0 ? (
          <div style={{
            fontFamily: FONT.sans,
            fontSize: 13,
            color: COLOR.muted,
            padding: `${SPACE.lg}px 0`,
          }}>
            No one to assign yet — invite cooks when scheduling the meal.
          </div>
        ) : (
          <ul style={{
            listStyle: "none",
            padding: 0,
            margin: `0 0 ${SPACE.section}px`,
            display: "flex",
            flexDirection: "column",
            gap: SPACE.xs,
          }}>
            {candidates.map(({ participant, existing }) => {
              const profile = profilesById?.get(participant.user_id) || null;
              const name = profile?.name || "Teammate";
              const palette = paletteEntry(participant.color_tag);
              const isSelected = selected.has(participant.user_id);
              const badge = existing ? statusBadge(existing.status) : null;
              return (
                <li key={participant.user_id}>
                  <button
                    type="button"
                    className="mcm-focusable"
                    onClick={() => toggle(participant.user_id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: SPACE.lg,
                      width: "100%",
                      padding: `${SPACE.md}px ${SPACE.lg}px`,
                      background: isSelected ? palette.bg : COLOR.ground,
                      border: `1px solid ${isSelected ? palette.ring : COLOR.border}`,
                      borderRadius: RADIUS.md,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <span style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: palette.bgSolid,
                      color: "#fff",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: FONT.mono,
                      fontSize: 12,
                      fontWeight: 600,
                      boxShadow: isSelected ? `0 0 0 2px ${palette.ring}` : "none",
                      flex: "0 0 auto",
                    }}>
                      {name.slice(0, 1).toUpperCase()}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{
                        display: "block",
                        fontFamily: FONT.forma,
                        fontSize: 14,
                        fontWeight: 600,
                        color: COLOR.ink,
                      }}>
                        {name}
                      </span>
                      <span style={{
                        display: "block",
                        fontFamily: FONT.mono,
                        fontSize: 10,
                        letterSpacing: 1,
                        color: COLOR.muted,
                        textTransform: "uppercase",
                        marginTop: 2,
                      }}>
                        {participant.role}
                        {participant.color_tag ? ` · ${palette.label}` : ""}
                      </span>
                    </span>
                    {badge ? (
                      <span style={{
                        fontFamily: FONT.mono,
                        fontSize: 9,
                        letterSpacing: 1.1,
                        padding: "3px 7px",
                        borderRadius: RADIUS.sm,
                        background: badge.bg,
                        color: badge.fg,
                        whiteSpace: "nowrap",
                      }}>
                        {badge.label}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <label style={{
          display: "block",
          fontFamily: FONT.mono,
          fontSize: 10,
          letterSpacing: 1.2,
          color: COLOR.muted,
          marginBottom: SPACE.xs,
        }}>
          DEADLINE
        </label>
        <input
          type="datetime-local"
          value={deadlineStr}
          onChange={(e) => setDeadlineStr(e.target.value)}
          style={FIELD_TIME_STYLE}
        />
        <div style={{
          fontFamily: FONT.sans,
          fontSize: 11,
          color: COLOR.muted,
          marginTop: SPACE.xs,
          marginBottom: SPACE.section,
        }}>
          Auto-computed from the meal time. Leave blank to skip reminders.
        </div>

        <div style={{ display: "flex", gap: SPACE.md }}>
          <button
            type="button"
            className="mcm-focusable"
            onClick={onClose}
            style={{
              flex: 1,
              fontFamily: FONT.mono,
              fontSize: 11,
              letterSpacing: 1.4,
              padding: `${SPACE.md}px 0`,
              background: COLOR.ground,
              color: COLOR.ink,
              border: `1px solid ${COLOR.border}`,
              borderRadius: RADIUS.md,
              cursor: "pointer",
            }}
          >
            CANCEL
          </button>
          <button
            type="button"
            className="mcm-focusable"
            onClick={confirm}
            disabled={busy || selected.size === 0}
            style={{
              flex: 2,
              fontFamily: FONT.mono,
              fontSize: 11,
              letterSpacing: 1.4,
              padding: `${SPACE.md}px 0`,
              background: selected.size === 0 ? COLOR.deep : COLOR.leaf,
              color: selected.size === 0 ? COLOR.muted : "#fff",
              border: "none",
              borderRadius: RADIUS.md,
              cursor: selected.size === 0 ? "default" : "pointer",
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy
              ? "SENDING…"
              : selected.size === 0
                ? "PICK SOMEONE"
                : `SEND TO ${selected.size}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
